import { Flex, Input } from '@chakra-ui/react';
import { useField } from 'formik';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useDateFormat } from '@/hooks';
import { KInput } from '@/ui-library/components/atoms/KInput';
import {
  DateSinglePicker,
  resolvePortalTarget,
  toDate,
  toDayString,
} from '@/design-system/date-picker';

dayjs.extend(customParseFormat);

/** `{ after }`/`{ before }` day-picker matchers, which is all these pass. */
type DayMatcher = { after?: Date; before?: Date };

/** The knobs the seven call sites pass; see the configs for who uses what. */
type FilterDateItem = {
  name: string;
  /** Lifts the no-future-dates cap. */
  isCustomize?: boolean;
  /** The host's own node, so the panel stays inside it. */
  containerRef?: React.RefObject<HTMLElement>;
  /** Store the formatted label rather than a `YYYY-MM-DD` day string. */
  isSetFormattedDate?: boolean;
  customOnChange?: (formatted: string) => void;
  customDisableDateValue?: DayMatcher | null;
  dayPickerProps?: { disabled?: DayMatcher };
  inputStyles?: Record<string, unknown>;
  inputProps?: Record<string, unknown>;
  /** 'v1' is the original Chakra input; anything else is `KInput`. */
  styleVersion?: 'v1' | 'v2';
  /** Read the trigger's text from `field.value` rather than the last pick. */
  hasFieldValue?: boolean;
  /** Both now inert: the picker owns the label and resolves the format. */
  skipDateSetting?: boolean;
  format?: string;
};

/**
 * The matchers only ever express a min and a max. `dayPickerProps` used to be
 * spread last and so won, and `customDisableDateValue` only applied under
 * `isCustomize` — both kept.
 */
const toBounds = (
  isCustomize: boolean,
  custom?: DayMatcher | null,
  dayPicker?: { disabled?: DayMatcher }
) => {
  const matcher = {
    ...(isCustomize && custom ? custom : {}),
    ...(dayPicker?.disabled ?? {}),
  };
  return {
    minDate: matcher.before,
    // Filters cannot look into the future unless the config says otherwise.
    maxDate: matcher.after ?? (isCustomize ? undefined : new Date()),
  };
};

/**
 * The `filterDatePicker` form field: a read-only date input that opens the
 * shared `DateSinglePicker`. Everything below is the Formik binding and the
 * per-config bounds and value shape — the panel itself is shared.
 */
export const FilterDatePicker = ({
  item,
  field,
  formik,
}: {
  item: FilterDateItem;
  field?: { value?: string | boolean | string[] };
  formik?: { submitCount?: number } | null;
}) => {
  const {
    name,
    isCustomize = false,
    containerRef,
    isSetFormattedDate = false,
    customOnChange,
    customDisableDateValue,
    dayPickerProps,
    inputStyles = {},
    inputProps = {},
    styleVersion = 'v1',
    hasFieldValue = false,
  } = item;
  const [, meta, helpers] = useField(name);
  const { setValue, setTouched, setError } = helpers;
  // `dateFormat.label` is the dayjs pattern the stored label is written and
  // read back with — `useSchDurationTimeFields` parses it with that same
  // pattern. It disagrees with `dateFnsFormat` when the account has no
  // `date_fmt`, so the two must not be mixed on a value.
  const { dateFormat } = useDateFormat();

  const raw = hasFieldValue ? field?.value : meta?.value;
  const stored = typeof raw === 'string' ? raw : undefined;
  // A formatted label parses only against the account pattern: `new Date()`
  // reads "01/09/2026" as 9 January.
  const selected = isSetFormattedDate
    ? (() => {
        const parsed = dayjs(meta?.value as string, dateFormat.label, true);
        return parsed.isValid() ? parsed.toDate() : undefined;
      })()
    : toDate(stored);
  const label = selected ? dayjs(selected).format(dateFormat.label) : '';
  const isError = Boolean(
    (meta?.touched || (formik?.submitCount ?? 0) > 0) &&
    meta?.error &&
    !field?.value
  );
  const { minDate, maxDate } = toBounds(
    isCustomize,
    customDisableDateValue,
    dayPickerProps
  );

  const commit = (picked: Date) => {
    const formatted = dayjs(picked).format(dateFormat.label);
    setValue(isSetFormattedDate ? formatted : toDayString(picked));
    setTouched(true);
    setError(undefined);
    customOnChange?.(formatted);
  };

  const TriggerInput = styleVersion === 'v1' ? Input : KInput;
  const triggerFontSize = styleVersion === 'v1' ? '12px' : '14px';

  return (
    <Flex flexDir="column" w="100%">
      <DateSinglePicker
        value={selected}
        onChange={commit}
        minDate={minDate}
        maxDate={maxDate}
        portalTarget={resolvePortalTarget(containerRef?.current)}
        renderTrigger={({ onClick }) => (
          <TriggerInput
            w="100%"
            fontSize={triggerFontSize}
            placeholder={dateFormat.label}
            value={label || stored || ''}
            id={name}
            isInvalid={isError}
            borderRadius="md"
            readOnly
            _hover={{ border: 'transparent' }}
            _focus={{ outline: 'none' }}
            onClick={onClick}
            onFocus={(event) => event.preventDefault()}
            {...inputStyles}
            {...(styleVersion === 'v1' ? {} : inputProps)}
          />
        )}
      />
    </Flex>
  );
};
